import defaultRule from './willChange_import_import';

export default {
    verb: 'willChange',
    namespace: 'import',
    relation: 'import',
    // Run before the default willChange so we can provide the dataModel
    // before column guessing occurs
    priority: defaultRule.priority + 10,
    description: 'Provide column model for denormalized order CSV import (Order #, SKU, Expected Qty)',
    logic: willChange
};

// SKU field to inject into the dataModel — not present in Blockly metadata
// because it's nested inside items[], but needed as a flat column for the
// denormalized CSV format (one row per Order # + SKU).
const SKU_FIELD = {
    _meta: {
        id: 'sku',
        hNodeType: 'ShortText',
        hNodeTypeGroup: 'formElement',
        title: 'SKU',
        propertyName: 'sku',
        alias: ['sku', 'sku #', 'sku number', 'item', 'product', 'upc', 'barcode', 'tag']
    }
};

// Expected quantity per SKU line (#1909). Like SKU, this lives inside items[] rather than
// in Blockly metadata, so it has to be injected as a flat column.
//
// The aliases are the spaced and abbreviated forms a warehouse export is likely to produce,
// matching how SKU_FIELD above is set up. The property name is camelCase like every other
// field in this domain, and no snake_case spelling is carried: guessColumns lowercases and
// strips non-alphanumerics before matching, so a header only has to resemble one of these.
//
// Left optional on purpose: an order CSV that predates this still imports, and a line with
// no quantity is one unit. doingValidate_import_import_order therefore still requires only
// Order # and SKU.
const EXPECTED_QTY_FIELD = {
    _meta: {
        id: 'expectedQty',
        hNodeType: 'Numeric',
        hNodeTypeGroup: 'formElement',
        title: 'Expected Qty',
        propertyName: 'expectedQty',
        dataType: { type: 'Integer' },
        // A blank cell means "not stated", which is one unit — not 0. Without this the shared
        // parser turned an empty cell into 0, `min: 1` then failed it, and the row was rejected
        // with "Expected Qty should be more than or equal to 1." for a cell the operator had
        // deliberately left empty. The "not stated" branch in willCreate_import_import_order
        // could never run.
        blankIsUnset: true,
        // And when it is not blank, it has to be digits. Without this the shared parser converted
        // the cell with `Number()` before the row ever left the browser, so '1e2', '0x10' and
        // '1.0' arrived at the server as 100, 16 and 1 — clean integers the stricter rule there
        // had nothing to object to. #2282 asks for `1.0` to fail, and it has to fail here to fail
        // at all.
        strictDigits: true,
        // No `min` here. The quantity is validated twice already — in the order import rule for
        // the preview, and by normalizeLineQuantities on the server, which is the one that
        // decides — and a third copy of the rule in the column definition is what turned a blank
        // cell into a range error before either of them was reached.
        alias: [
            'expected qty',
            'expectedqty',
            'expected quantity',
            'qty',
            'quantity',
            'order qty',
            'ordered qty',
            'qty ordered',
            'line qty'
        ]
    }
};

// Aliases for the title field so "Order #" CSV headers match automatically
const TITLE_ALIASES = ['order #', 'order number', 'order no', 'orderno', 'order_number', 'order', 'order id', 'order_id'];

function willChange({ data }) {
    const { propertyName, newRecord } = data;
    if (propertyName !== 'file') return data;
    if (newRecord.foreignNamespace !== 'order' || newRecord.foreignRelation !== 'order') return data;

    // Add aliases to the existing title field so "Order #" headers auto-match
    const titleField = newRecord.dataModel?.find(f => f._meta?.propertyName === 'title');
    if (titleField) {
        titleField._meta.alias = TITLE_ALIASES;
    }

    // Inject the flat item fields if not already in the dataModel
    for (const field of [SKU_FIELD, EXPECTED_QTY_FIELD]) {
        if (!newRecord.dataModel?.find(f => f._meta?.propertyName === field._meta.propertyName)) {
            newRecord.dataModel = [...(newRecord.dataModel || []), field];
        }
    }

    return { ...data, newRecord };
}
